import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import Header from '@/components/Header';
import { ROUTES } from '@/utils/constants';
import { useAuthStore } from '@/store/useAuthStore';
import { authService } from '@/services/authService';
import toast from 'react-hot-toast';

const DashboardLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { firebaseUser, userProfile, logout, setUserProfile } = useAuthStore();

  // Refresh user profile when dashboard loads to ensure plan is up to date
  useEffect(() => {
    if (firebaseUser) {
      authService.getBackendUserProfile().then((backendProfile) => {
        if (backendProfile) {
          // Merge with existing profile to preserve Firestore data
          const mergedProfile = {
            ...(userProfile || {}),
            ...backendProfile,
            uid: firebaseUser.uid,
            email: firebaseUser.email || backendProfile.email || '',
            displayName: userProfile?.displayName || backendProfile.displayName || firebaseUser.displayName || '',
          };
          setUserProfile(mergedProfile);
        }
      }).catch((error) => {
        console.warn('Failed to refresh user profile:', error);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firebaseUser?.uid]); // Only run when user ID changes (on mount or user switch)

  const handleLogout = async () => {
    try {
      await authService.signOut();
      logout();
      navigate(ROUTES.HOME);
      toast.success('Logged out successfully');
    } catch (error) {
      toast.error('Failed to logout');
    }
  };

  // Check if user has Business or Enterprise plan
  const hasVoiceAssistantAccess = userProfile?.plan && ['business', 'enterprise'].includes(userProfile.plan);

  const navItems = [
    { path: ROUTES.DASHBOARD, label: 'Overview', icon: '📊' },
    { path: ROUTES.API_KEYS, label: 'API Keys', icon: '🔑' },
    { path: `${ROUTES.DASHBOARD}/api-docs`, label: 'API Docs & Sandbox', icon: '📖' },
    { path: ROUTES.USAGE, label: 'Usage', icon: '📈' },
    // Only show Voice Assistant for Business/Enterprise plans
    ...(hasVoiceAssistantAccess ? [{ path: ROUTES.VOICE_ASSISTANT, label: 'Ajebota Voice Assistant', icon: '🎙️' }] : []),
  ];

  const isActive = (path: string) => {
    if (path === ROUTES.DASHBOARD) {
      return location.pathname === ROUTES.DASHBOARD;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="main-page-wrapper">
      <Header />
      
      <div className="container pt-150 lg-pt-80 pb-100 lg-pb-80 mt-30 lg-mt-20">
        <div className="row">
          {/* Sidebar */}
          <div className="col-lg-3 col-xl-2">
            <div className="card-style-one border-30 bg-white sticky-top" style={{ top: '100px' }}>
              <nav>
                <ul className="style-none">
                  {navItems.map((item) => (
                    <li key={item.path} className="mb-10">
                      <Link
                        to={item.path}
                        className={`d-flex align-items-center text-decoration-none ${isActive(item.path) ? 'text-primary fw-600' : 'text-dark'}`}
                        style={{
                          padding: '10px 15px',
                          borderRadius: '8px',
                          transition: 'all 0.2s',
                          backgroundColor: isActive(item.path) ? 'rgba(0, 123, 255, 0.1)' : 'transparent'
                        }}
                      >
                        <span className="me-2" style={{ fontSize: '18px' }}>{item.icon}</span>
                        <span>{item.label}</span>
                      </Link>
                    </li>
                  ))}
                  <li className="mt-4 pt-4 border-top">
                    <div className="d-flex align-items-center mb-3">
                      <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-2" style={{ width: '32px', height: '32px', fontSize: '14px' }}>
                        {userProfile?.displayName?.[0]?.toUpperCase() || firebaseUser?.email?.[0]?.toUpperCase() || 'U'}
                      </div>
                      <div className="flex-fill">
                        <div className="fw-500 text-dark" style={{ fontSize: '14px' }}>
                          {userProfile?.displayName || firebaseUser?.email?.split('@')[0] || 'User'}
                        </div>
                        <div className="text-dark" style={{ fontSize: '12px', opacity: 0.7 }}>
                          {userProfile?.plan 
                            ? userProfile.plan.charAt(0).toUpperCase() + userProfile.plan.slice(1)
                            : 'Free'} plan
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={handleLogout}
                      className="btn-two w-100"
                      style={{ fontSize: '14px' }}
                    >
                      Logout
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="col-lg-9 col-xl-10">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;

